'use client';

import React, { useState, useMemo } from 'react';
import {
  ImageIcon,
  FileTextIcon,
  VideoIcon,
  DownloadIcon,
  TrashIcon,
  DotsHorizontalIcon,
  MagnifyingGlassIcon,
  EyeOpenIcon,
  Share1Icon,
  Pencil1Icon,
} from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Stack } from '../stack';
import { Button } from '../button';
import { IconButton } from '../icon-button';
import { Input } from '../input';
import { Select } from '../select';
import { Checkbox } from '../checkbox';
import { Badge } from '../badge';
import { Dropdown } from '../dropdown';

export interface FileItem {
  id: string;
  name: string;
  size: number;
  type: string;
  modifiedAt: Date;
  createdAt: Date;
  thumbnail?: string;
  tags?: string[];
  shared?: boolean;
  owner?: string;
}

export interface FileListProps extends BoxProps {
  /** List of files to display */
  files: FileItem[];
  /** View mode */
  view?: 'list' | 'grid';
  /** Whether to show checkboxes for selection */
  selectable?: boolean;
  /** Selected file IDs */
  selectedIds?: string[];
  /** Sort field */
  sortBy?: 'name' | 'size' | 'type' | 'modifiedAt';
  /** Sort order */
  sortOrder?: 'asc' | 'desc';
  /** Filter by file type */
  filterType?: string;
  /** Search query */
  searchQuery?: string;
  /** Callback when file is clicked */
  onFileClick?: (file: FileItem) => void;
  /** Callback when files are selected */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Callback when file action is triggered */
  onFileAction?: (action: string, file: FileItem) => void;
  /** Callback when sort changes */
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
}

// Helper function to categorize files
const getFileCategory = (type: string): string => {
  if (type.startsWith('image/')) return 'Images';
  if (type.startsWith('video/')) return 'Videos';
  if (type === 'application/pdf') return 'PDFs';
  if (type.startsWith('text/')) return 'Documents';
  return 'Other';
};

/**
 * FileList component - displays files in list or grid view with sorting and filtering
 */
export const FileList: React.FC<FileListProps> = ({
  files,
  view = 'list',
  selectable = false,
  selectedIds = [],
  sortBy = 'name',
  sortOrder = 'asc',
  filterType,
  searchQuery = '',
  onFileClick,
  onSelectionChange,
  onFileAction,
  onSortChange,
  ...boxProps
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [localFilterType, setLocalFilterType] = useState(filterType || 'all');

  // Get unique file types
  const fileTypes = useMemo(() => {
    const types = new Set(files.map((f) => getFileCategory(f.type)));
    return Array.from(types);
  }, [files]);

  // Filter and sort files
  const processedFiles = useMemo(() => {
    let result = [...files];

    // Filter by search query
    if (localSearchQuery) {
      result = result.filter((file) =>
        file.name.toLowerCase().includes(localSearchQuery.toLowerCase())
      );
    }

    // Filter by type
    if (localFilterType && localFilterType !== 'all') {
      result = result.filter((file) => getFileCategory(file.type) === localFilterType);
    }

    // Sort files
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'modifiedAt':
          comparison = a.modifiedAt.getTime() - b.modifiedAt.getTime();
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [files, localSearchQuery, localFilterType, sortBy, sortOrder]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return ImageIcon;
    if (type.startsWith('video/')) return VideoIcon;
    if (type === 'application/pdf' || type.startsWith('text/')) return FileTextIcon;
    return FileIcon;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange?.(processedFiles.map((f) => f.id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectFile = (fileId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, fileId]);
    } else {
      onSelectionChange?.(selectedIds.filter((id) => id !== fileId));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      onSortChange?.(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange?.(field, 'asc');
    }
  };

  const FileIcon = ({ file }: { file: FileItem }) => {
    const Icon = getFileIcon(file.type);
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={10}
        height={10}
        backgroundColor="bg.muted"
        borderRadius="md"
        color="fg.muted"
      >
        <Icon file={file} />
      </Box>
    );
  };

  return (
    <Box {...boxProps}>
      {/* Search and filters */}
      <Box marginBottom={4}>
        <Stack direction="row" gap={3} align="center">
          <Box position="relative" flex={1} maxWidth="400px">
            <Box
              position="absolute"
              left={3}
              top="50%"
              transform="translateY(-50%)"
              color="fg.muted"
              pointerEvents="none"
            >
              <MagnifyingGlassIcon />
            </Box>
            <Input
              paddingLeft={10}
              placeholder="Search files..."
              value={localSearchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLocalSearchQuery(e.target.value)
              }
            />
          </Box>

          <Select
            value={localFilterType}
            onChange={setLocalFilterType}
            options={[
              { value: 'all', label: 'All Files' },
              ...fileTypes.map((type) => ({ value: type, label: type })),
            ]}
            selectSize="sm"
          />

          {selectedIds.length > 0 && <Badge>{selectedIds.length} selected</Badge>}
        </Stack>
      </Box>

      {/* List view */}
      {view === 'list' && (
        <Box>
          {/* Table header */}
          <Box
            display="grid"
            gridTemplateColumns={
              selectable ? '40px 1fr 120px 120px 180px 80px' : '1fr 120px 120px 180px 80px'
            }
            gap={3}
            padding={3}
            backgroundColor="bg.muted"
            borderRadius="md"
            marginBottom={2}
          >
            {selectable && (
              <Checkbox
                checked={selectedIds.length === processedFiles.length && processedFiles.length > 0}
                onChange={handleSelectAll}
              />
            )}
            <Button variant="ghost" size="sm" onClick={() => handleSort('name')}>
              Name
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleSort('size')}>
              Size
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleSort('type')}>
              Type
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleSort('modifiedAt')}>
              Modified
            </Button>
            <Text fontSize="sm" color="fg.muted">
              Actions
            </Text>
          </Box>

          {/* File rows */}
          <Stack gap={1}>
            {processedFiles.map((file) => (
              <Box
                key={file.id}
                display="grid"
                gridTemplateColumns={
                  selectable ? '40px 1fr 120px 120px 180px 80px' : '1fr 120px 120px 180px 80px'
                }
                gap={3}
                padding={3}
                backgroundColor="bg.surface"
                borderRadius="md"
                border="1px solid"
                borderColor="border.muted"
                transition="all 0.2s"
                _hover={{
                  borderColor: 'border.default',
                  backgroundColor: 'bg.muted',
                }}
              >
                {selectable && (
                  <Checkbox
                    checked={selectedIds.includes(file.id)}
                    onChange={(checked) => handleSelectFile(file.id, checked)}
                  />
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={3}
                  cursor="pointer"
                  onClick={() => onFileClick?.(file)}
                >
                  <FileIcon file={file} />
                  <Box flex={1}>
                    <Text fontWeight="medium">{file.name}</Text>
                    {file.tags && file.tags.length > 0 && (
                      <Box display="flex" gap={1} marginTop={1}>
                        {file.tags.map((tag) => (
                          <Badge key={tag} size="sm">
                            {tag}
                          </Badge>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {file.shared && <Share1Icon />}
                </Box>
                <Text fontSize="sm" color="fg.muted">
                  {formatFileSize(file.size)}
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  {getFileCategory(file.type)}
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  {formatDate(file.modifiedAt)}
                </Text>
                <Box display="flex" gap={1}>
                  <Dropdown
                    items={[
                      {
                        id: 'view',
                        label: 'View',
                        icon: <EyeOpenIcon />,
                        onSelect: () => onFileAction?.('view', file),
                      },
                      {
                        id: 'download',
                        label: 'Download',
                        icon: <DownloadIcon />,
                        onSelect: () => onFileAction?.('download', file),
                      },
                      {
                        id: 'rename',
                        label: 'Rename',
                        icon: <Pencil1Icon />,
                        onSelect: () => onFileAction?.('rename', file),
                      },
                      {
                        id: 'share',
                        label: 'Share',
                        icon: <Share1Icon />,
                        onSelect: () => onFileAction?.('share', file),
                      },
                      {
                        id: 'separator',
                        label: '',
                      },
                      {
                        id: 'delete',
                        label: 'Delete',
                        icon: <TrashIcon />,
                        destructive: true,
                        onSelect: () => onFileAction?.('delete', file),
                      },
                    ]}
                    trigger={
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="File actions"
                        icon={<DotsHorizontalIcon />}
                      />
                    }
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Grid view */}
      {view === 'grid' && (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
          {processedFiles.map((file) => {
            const Icon = getFileIcon(file.type);
            return (
              <Box
                key={file.id}
                padding={4}
                backgroundColor="bg.surface"
                borderRadius="lg"
                border="1px solid"
                borderColor="border.muted"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  borderColor: 'border.default',
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                onClick={() => onFileClick?.(file)}
              >
                {selectable && (
                  <Box marginBottom={2}>
                    <Checkbox
                      checked={selectedIds.includes(file.id)}
                      onChange={(checked) => handleSelectFile(file.id, checked)}
                    />
                  </Box>
                )}

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="120px"
                  marginBottom={3}
                  backgroundColor="bg.muted"
                  borderRadius="md"
                  fontSize="4xl"
                  color="fg.muted"
                >
                  {file.thumbnail ? (
                    <img
                      src={file.thumbnail}
                      alt={file.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 'inherit',
                      }}
                    />
                  ) : (
                    <Icon file={file} />
                  )}
                </Box>

                <Text fontWeight="medium" marginBottom={1}>
                  {file.name}
                </Text>

                <Text fontSize="sm" color="fg.muted" marginBottom={2}>
                  {formatFileSize(file.size)}
                </Text>

                {file.tags && file.tags.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {file.tags.map((tag) => (
                      <Badge key={tag} size="sm">
                        {tag}
                      </Badge>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Empty state */}
      {processedFiles.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          padding={8}
          backgroundColor="bg.muted"
          borderRadius="lg"
          textAlign="center"
        >
          <Box fontSize="4xl" color="fg.muted" marginBottom={3}>
            <FileTextIcon />
          </Box>
          <Text fontWeight="medium" marginBottom={1}>
            No files found
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {localSearchQuery ? 'Try adjusting your search' : 'Upload some files to get started'}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default FileList;
